import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { LOGO_DARK, LOGO_LIGHT } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, ChevronDown } from "lucide-react";

const HERO =
  "https://images.unsplash.com/photo-1672552226380-486fe900b322?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwbG9naXN0aWNzJTIwd2FyZWhvdXNlJTIwZGFya3xlbnwwfHx8fDE3ODI0MDI2NDV8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(params.get("error") || "");
  const [loading, setLoading] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block">
        <img src={HERO} alt="PHS logistics warehouse" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src={LOGO_LIGHT} alt="PHS Freight" className="h-11 w-auto object-contain brightness-0 invert" />
          </div>
          <div className="max-w-md">
            <h1 className="font-heading text-5xl font-black leading-none tracking-tighter text-white">Freight Command Center</h1>
            <p className="mt-5 text-base leading-relaxed text-zinc-300">
              Pallet stack engineering, density-based freight class, FedEx FAK rating, and live carrier comparison — built for Premier Handling Solutions.
            </p>
            <div className="mt-8 flex items-center gap-2 text-sm text-zinc-400">
              <ShieldCheck className="h-4 w-4 text-[hsl(var(--success))]" />
              Team access restricted to @phsinc.com
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 lg:hidden flex items-center gap-3">
            <img src={LOGO_DARK} alt="PHS Freight" className="h-9 w-auto object-contain block dark:hidden" />
            <img src={LOGO_LIGHT} alt="PHS Freight" className="h-9 w-auto object-contain hidden dark:block brightness-0 invert" />
          </div>
          <h2 className="font-heading text-3xl font-bold tracking-tight">Sign in</h2>
          <p className="mt-2 text-sm text-muted-foreground">Use your @phsinc.com Google Workspace account.</p>

          {error && (
            <div className="mt-4 rounded-md border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 px-3 py-2 text-sm text-[hsl(var(--destructive))]" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            onClick={googleLogin}
            data-testid="google-login-button"
            className="mt-6 h-11 w-full gap-2 bg-white text-zinc-900 hover:bg-zinc-100"
          >
            <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google Workspace
          </Button>

          <button
            onClick={() => setShowAdmin((s) => !s)}
            className="mt-6 flex w-full items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            data-testid="toggle-admin-login"
          >
            Admin sign-in (email & password) <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAdmin ? "rotate-180" : ""}`} />
          </button>

          {showAdmin && (
            <form onSubmit={submit} className="mt-4 space-y-4 border-t border-border pt-4">
              <div>
                <Label className="label-caps">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" data-testid="login-email-input" required />
              </div>
              <div>
                <Label className="label-caps">Password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11" data-testid="login-password-input" required />
              </div>
              <Button type="submit" disabled={loading} className="h-11 w-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" data-testid="login-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </Button>
            </form>
          )}

          <p className="mt-8 text-center text-xs text-muted-foreground">Premier Handling Solutions • PHS Lift — internal tool</p>
        </div>
      </div>
    </div>
  );
}
